import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, fmt } from "../client";
import { PageHeader, Panel, Pill, Empty } from "../ui";

const Media = ({ url, id }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative h-36 w-full overflow-hidden rounded-lg bg-black sm:w-48 sm:shrink-0">
      <img src={url} alt="Flagged media" className={`h-full w-full object-cover transition-[filter] duration-300 ${show ? "" : "blur-2xl"}`} />
      <button onClick={() => setShow(!show)} data-testid={`report-reveal-${id}`}
        className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-black/70 px-3 py-1 text-xs backdrop-blur">
        {show ? <EyeOff size={12} /> : <Eye size={12} />} {show ? "Hide" : "Reveal media"}
      </button>
    </div>
  );
};

const ReportCard = ({ r, onAct, closed }) => (
  <Panel className="s-rise flex flex-col gap-5 p-5 sm:flex-row" data-testid={`report-card-${r.id}`}>
    {r.media_url && <Media url={r.media_url} id={r.id} />}
    <div className="flex-1 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={r.kind === "media" ? "warn" : "dim"}>{r.kind === "media" ? "Media" : "Profile / chat"}</Pill>
        <span className="font-medium">{r.category || "Uncategorized"}</span>
        {closed && <Pill tone="ok">{r.status}</Pill>}
      </div>
      {r.details && <p className="text-sm text-[var(--s-dim)]">{r.details}</p>}
      <p className="s-mono text-xs text-[var(--s-dim)]">reporter {r.reporter || "—"} → offender {r.target || "—"} · {fmt(r.created_at)}</p>
    </div>
    {!closed && (
      <div className="flex gap-2 sm:flex-col">
        <Button size="sm" variant="outline" className="s-ghost" onClick={() => onAct(r, "dismiss")} data-testid={`report-dismiss-${r.id}`}>Dismiss</Button>
        <Button size="sm" variant="outline" className="s-warn" onClick={() => onAct(r, "warn")} data-testid={`report-warn-${r.id}`}>Issue warning</Button>
        <Button size="sm" variant="outline" className="s-danger" onClick={() => onAct(r, "ban")} data-testid={`report-ban-${r.id}`}>Ban offender</Button>
      </div>
    )}
  </Panel>
);

export default function Reports() {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { api(`/staff/reports?status=${status}`).then(setRows).catch((e) => toast.error(e.message)); }, [status]);
  useEffect(() => { setRows(null); load(); }, [load]);
  const onAct = async (r, action) => {
    const note = window.prompt(action === "ban" ? "Ban reason (required)" : "Optional note", "");
    if (note === null || (action === "ban" && note.trim().length < 3)) return;
    try { await api(`/staff/reports/${r.kind}/${r.id}/${action}`, { method: "POST", body: { note } }); toast.success("Report closed"); load(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div>
      <PageHeader kicker="Moderation queue" title="User reports">
        <Tabs value={status} onValueChange={setStatus}><TabsList>
          <TabsTrigger value="open" data-testid="reports-tab-open">Open</TabsTrigger>
          <TabsTrigger value="closed" data-testid="reports-tab-closed">Closed</TabsTrigger>
        </TabsList></Tabs>
      </PageHeader>
      <div className="space-y-3" data-testid="reports-list">
        {rows === null ? <p className="text-sm text-[var(--s-dim)]">Loading…</p> : rows.length === 0 ? <Empty>Queue is clear.</Empty>
          : rows.map((r) => <ReportCard key={`${r.kind}-${r.id}`} r={r} onAct={onAct} closed={status === "closed"} />)}
      </div>
    </div>
  );
}
